// The attention feed: everything this daemon needs a human for — decisions,
// plan approvals, review accepts, audit checks — with the actions inline.
// This is deliberately NOT the kanban; the phone is for judgment calls.
import { el, clear, isInteracting } from "../dom.js";
import { commentRow } from "./comment.js";
import { diffView } from "../diff.js";
import { gitPush } from "../git.js";
import { Connection } from "../relay.js";
import { syncPush } from "../push.js";
import { notificationBell } from "../bell.js";
import { pendingCount, notifyNewAttention } from "./notifications.js";
import { bottomNav } from "../nav.js";
import { projectTitle } from "../topbar.js";
import type { DaemonEntry } from "../store.js";
import type { Attention, Attempt, Comment, Decision, Evidence, Plan, ReviewItem } from "../types.js";
import { taskDetailRows } from "./plan-task.js";
import { STAGE_ORDER } from "../types.js";

const REFETCH_DEBOUNCE_MS = 500;

export function renderFeed(
  root: HTMLElement,
  entry: DaemonEntry,
  onSwitch: () => void,
  onBacklog: () => void,
  onSettings: () => void,
  onNotifications: () => void,
): () => void {
  clear(root);

  const dot = el("span", { class: "status-dot" });
  const banner = el("div", {});
  const composeSlot = el("div", {});
  const feed = el("div", {}, [el("div", { class: "empty" }, ["Connecting…"])]);

  const pushBtn = el("button", {}, ["Push"]) as HTMLButtonElement;
  pushBtn.onclick = async () => {
    if (!conn) {
      banner.replaceChildren(el("div", { class: "error-banner" }, ["Not connected to the daemon yet."]));
      return;
    }
    pushBtn.disabled = true;
    try {
      await gitPush(conn);
      banner.replaceChildren(el("div", { class: "notice" }, ["Pushed to remote."]));
    } catch (e) {
      banner.replaceChildren(el("div", { class: "error-banner" }, [(e as Error).message]));
    }
    pushBtn.disabled = false;
  };

  // The bell carries the pending count and opens the shared Notifications page.
  let bell = notificationBell({ count: 0, onClick: onNotifications });
  const updateBell = (count: number) => {
    const next = notificationBell({ count, onClick: onNotifications });
    bell.replaceWith(next);
    bell = next;
  };

  root.append(
    el("header", { class: "topbar" }, [
      projectTitle(dot, entry.name, null, onSwitch),
      el("button", { class: "primary", onclick: () => toggleCompose() }, ["＋ New"]),
      pushBtn,
      bell,
    ]),
    banner,
    composeSlot,
    feed,
    bottomNav({ active: "feed", onFeed: () => {}, onBoard: onBacklog, onSettings }),
  );

  let conn: Connection | null = null;
  let stopped = false;
  let refetchTimer = 0;
  let reconnectDelay = 1000;
  let lastAttention: Attention | null = null;

  const setOnline = (online: boolean) => {
    dot.className = `status-dot ${online ? "online" : "offline"}`;
  };

  const refetch = async () => {
    if (!conn) return;
    try {
      const a = await conn.api<Attention>("GET", "/api/attention");
      void notifyNewAttention(entry.name, lastAttention, a);
      lastAttention = a;
      updateBell(pendingCount(a));
      renderAttention(feed, a, conn);
      banner.replaceChildren();
    } catch (e) {
      banner.replaceChildren(el("div", { class: "error-banner" }, [(e as Error).message]));
    }
  };

  const scheduleRefetch = () => {
    clearTimeout(refetchTimer);
    refetchTimer = window.setTimeout(() => {
      // Don't rebuild the feed out from under someone reading or typing — retry
      // on the next tick so the update still lands once they're done.
      if (isInteracting(feed)) return scheduleRefetch();
      void refetch();
    }, REFETCH_DEBOUNCE_MS);
  };

  // Compose: define a BigTask from the phone. "Plan" hands it to the planner
  // (the proposed plan comes back into this feed for approval); "Backlog"
  // parks the idea untouched.
  let composeOpen = false;
  const toggleCompose = () => {
    composeOpen = !composeOpen;
    composeSlot.replaceChildren();
    if (!composeOpen) return;

    const title = el("input", { type: "text", placeholder: "What should get built? (outcome)" }) as HTMLInputElement;
    const intent = el("textarea", { placeholder: "Why — context, constraints, desired outcome…" }) as HTMLTextAreaElement;
    const err = el("div", {});
    const submit = (btn: HTMLButtonElement, status?: string) => async () => {
      err.replaceChildren();
      if (!conn) {
        err.replaceChildren(el("div", { class: "error-banner" }, ["Not connected to the daemon yet."]));
        return;
      }
      if (!title.value.trim()) {
        err.replaceChildren(el("div", { class: "error-banner" }, ["Give it a title first."]));
        return;
      }
      btn.disabled = true;
      try {
        await conn.api("POST", "/api/bigtasks", {
          title: title.value.trim(),
          intent: intent.value.trim(),
          ...(status ? { status } : {}),
        });
        toggleCompose();
        banner.replaceChildren(
          el("div", { class: "notice" }, [
            status === "backlog"
              ? "Parked in the backlog."
              : "Sent to the planner — the proposed plan will show up here for approval.",
          ]),
        );
        scheduleRefetch();
      } catch (e) {
        btn.disabled = false;
        err.replaceChildren(el("div", { class: "error-banner" }, [(e as Error).message]));
      }
    };
    const planBtn = el("button", { class: "primary" }, ["Create & plan"]) as HTMLButtonElement;
    planBtn.onclick = () => void submit(planBtn)();
    const backlogBtn = el("button", {}, ["Backlog"]) as HTMLButtonElement;
    backlogBtn.onclick = () => void submit(backlogBtn, "backlog")();

    composeSlot.append(
      el("div", { class: "card" }, [
        el("h3", {}, ["New big task"]),
        el("div", { class: "field" }, [title]),
        el("div", { class: "field" }, [intent]),
        err,
        el("div", { class: "actions" }, [
          planBtn,
          backlogBtn,
          el("button", { onclick: () => toggleCompose() }, ["Cancel"]),
        ]),
      ]),
    );
    title.focus();
  };

  const connect = async () => {
    if (stopped) return;
    banner.replaceChildren();
    const c = new Connection(entry, {
      onEvent: () => scheduleRefetch(),
      onClose: () => {
        setOnline(false);
        if (stopped) return;
        banner.replaceChildren(el("div", { class: "notice" }, ["Connection lost — reconnecting…"]));
        window.setTimeout(() => void connect(), reconnectDelay);
        reconnectDelay = Math.min(reconnectDelay * 2, 30_000);
      },
    });
    try {
      await c.connect();
      conn = c;
      reconnectDelay = 1000;
      setOnline(true);
      void syncPush(c);
      await refetch();
    } catch (e) {
      setOnline(false);
      clear(feed);
      feed.append(el("div", { class: "empty" }, ["Daemon unreachable."]));
      banner.replaceChildren(
        el("div", { class: "error-banner" }, [(e as Error).message]),
        el("div", { class: "actions" }, [el("button", { onclick: () => void connect() }, ["Retry"])]),
      );
    }
  };
  void connect();

  // Returned cleanup runs when the router navigates away.
  return () => {
    stopped = true;
    clearTimeout(refetchTimer);
    conn?.close();
  };
}

export function renderAttention(feed: HTMLElement, a: Attention, conn: Connection): void {
  clear(feed);
  // Daemons newer than 2026-06 already filter to proposed; keep the guard for
  // older ones so settled plans never clutter the phone.
  a.plans = a.plans.filter((p) => p.status === "proposed");
  const total = a.decisions.length + a.plans.length + a.reviews.length + a.audits.length;
  if (total === 0) {
    feed.append(el("div", { class: "empty" }, ["All clear — nothing needs you right now. 🏭"]));
    return;
  }

  const act = (label: string, cls: string, fn: () => Promise<unknown>): HTMLElement => {
    const btn = el("button", { class: cls }, [label]) as HTMLButtonElement;
    btn.onclick = async () => {
      btn.disabled = true;
      try {
        await fn();
      } catch (e) {
        alert((e as Error).message);
        btn.disabled = false;
      }
    };
    return btn;
  };

  if (a.decisions.length) {
    feed.append(section("Decisions", a.decisions.length));
    for (const d of a.decisions) feed.append(decisionCard(d, conn, act));
  }
  if (a.plans.length) {
    feed.append(section("Plans awaiting approval", a.plans.length));
    for (const p of a.plans) feed.append(planCard(p, conn, act));
  }
  if (a.reviews.length) {
    feed.append(section("Reviews", a.reviews.length));
    for (const r of a.reviews) feed.append(reviewCard(r, conn, act));
  }
  if (a.audits.length) {
    feed.append(section("Audits", a.audits.length));
    for (const r of a.audits) feed.append(auditCard(r, conn, act));
  }
}

type Act = (label: string, cls: string, fn: () => Promise<unknown>) => HTMLElement;

function section(title: string, count: number): HTMLElement {
  return el("div", { class: "section-title" }, [title, el("span", { class: "badge count" }, [String(count)])]);
}

function decisionCard(d: Decision, conn: Connection, act: Act): HTMLElement {
  const promote = el("input", { type: "checkbox" }) as HTMLInputElement;
  const custom = el("input", { type: "text", placeholder: "Custom answer…" }) as HTMLInputElement;
  const answer = (text: string) =>
    conn.api("POST", `/api/decisions/${d.id}/answer`, { answer: text, promote: promote.checked });

  const options = (d.options ?? []).map((o) => act(o, "primary", () => answer(o)));
  return el("div", { class: "card" }, [
    el("h3", {}, [d.question]),
    d.context ? el("p", { class: "meta" }, [d.context]) : "",
    el("div", { class: "actions" }, options),
    el("div", { class: "actions" }, [
      custom,
      act("Answer", "", async () => {
        if (!custom.value.trim()) throw new Error("Type an answer first.");
        await answer(custom.value.trim());
      }),
    ]),
    el("label", { class: "meta" }, [promote, " Promote answer to a standing convention"]),
  ]);
}

function planCard(p: Plan, conn: Connection, act: Act): HTMLElement {
  const feedback = el("textarea", { placeholder: "What should the planner rethink?" }) as HTMLTextAreaElement;
  const details = el("details", {}, [
    el("summary", {}, [`${p.tasks?.length ?? 0} tasks`]),
    el("div", {}, (p.tasks ?? []).map((t) =>
      el("div", {}, [
        el("p", { class: "meta" }, [`• ${t.title}`]),
        ...taskDetailRows(t).map((r) => el("p", { class: "meta" }, [`  ${r.label}: ${r.value}`])),
      ]),
    )),
  ]);
  return el("div", { class: "card" }, [
    el("h3", {}, [p.bigTask?.title ?? "Plan"]),
    p.bigTask?.intent ? el("p", { class: "meta" }, [p.bigTask.intent]) : "",
    details,
    el("div", { class: "actions" }, [
      act("Approve", "approve", () => conn.api("POST", `/api/plans/${p.id}/approve`)),
      act("Reject", "danger", () => conn.api("POST", `/api/plans/${p.id}/reject`)),
      act("Revise", "", async () => {
        if (!feedback.value.trim()) throw new Error("Give the planner some feedback first.");
        await conn.api("POST", `/api/plans/${p.id}/revise`, { feedback: feedback.value.trim() });
      }),
    ]),
    feedback,
  ]);
}

function reviewCard(r: ReviewItem, conn: Connection, act: Act): HTMLElement {
  const t = r.task;
  const green = t.status === "review";
  // An advisory failure is a gate that failed but doesn't hard-block (e.g.
  // mutation testing). When present, accepting requires the force/merge-anyway
  // path so the human signs off on shipping over a red gate.
  const hasFailure = r.attempt?.evidence?.stages
    ? Object.values(r.attempt.evidence.stages).some((s) => !s.pass && !s.skipped)
    : false;
  const input = el("textarea", { placeholder: "Reason / guidance…" }) as HTMLTextAreaElement;
  const need = (): string => {
    if (!input.value.trim()) throw new Error("Write a short note first.");
    return input.value.trim();
  };
  // Merge anyway needs a confirm before the force-accept; build it directly so a
  // dismissed confirm just re-enables the button instead of alerting like `act`.
  const mergeAnyway = (): HTMLElement => {
    const btn = el("button", { class: "danger" }, ["Merge anyway"]) as HTMLButtonElement;
    btn.onclick = async () => {
      if (!confirm(`Gates failed on "${t.title}". Merge its work into the base branch anyway?`)) return;
      btn.disabled = true;
      try {
        await conn.api("POST", `/api/tasks/${t.id}/accept`, { force: true });
      } catch (e) {
        alert((e as Error).message);
        btn.disabled = false;
      }
    };
    return btn;
  };

  return el("div", { class: "card" }, [
    el("h3", {}, [t.title]),
    el("p", { class: "meta" }, [`${t.status} · ${t.riskTier} risk${t.branch ? ` · ${t.branch}` : ""}`]),
    t.spec ? el("details", {}, [el("summary", {}, ["Spec"]), el("p", { class: "meta" }, [t.spec])]) : "",
    // Verification pills: same gate stages and order as the desktop UI, so the
    // accept/reject call carries the same at-a-glance signal.
    r.attempt ? stageRow(r.attempt.evidence) : "",
    // When something went red, surface *why* — the failing stage's output and
    // the agent log — instead of a blind accept/reject.
    !green && r.attempt ? failureBlock(r.attempt) : "",
    usageLine(r.attempt?.usage),
    // The judgment IS the diff: when the work touched code, show exactly what
    // would merge so accept/reject isn't a blind call.
    r.attempt?.evidence?.diff ? diffView(r.attempt.evidence.diff) : "",
    el("div", { class: "actions" }, [
      ...(green
        ? [
            hasFailure
              ? mergeAnyway()
              : act("Accept & merge", "approve", () => conn.api("POST", `/api/tasks/${t.id}/accept`)),
            // Daemon contract: POST /api/tasks/:id/request-changes with body { reason }.
            // Using "reason" (same key as /reject) — "guidance" caused daemon-side crashes.
            act("Request changes", "", () => conn.api("POST", `/api/tasks/${t.id}/request-changes`, { reason: need() })),
            act("Reject", "danger", () => conn.api("POST", `/api/tasks/${t.id}/reject`, { reason: need() })),
          ]
        : [
            act("Retry", "primary", () => conn.api("POST", `/api/tasks/${t.id}/retry`)),
            ...(t.branch ? [mergeAnyway()] : []),
            act("Reject", "danger", () => conn.api("POST", `/api/tasks/${t.id}/reject`, { reason: need() })),
          ]),
    ]),
    input,
    commentsBlock(t.id, conn),
  ]);
}

// stageRow renders the gate verification pills — one chip per stage that ran,
// in run order, coloured pass/fail/skip. Failing chips are tappable to expand
// their captured output inline (the desktop UI does the same via tooltip).
function stageRow(ev: Evidence | undefined): HTMLElement | string {
  const stages = ev?.stages ?? {};
  const chips = STAGE_ORDER.filter((s) => stages[s]).map((s) => {
    const res = stages[s];
    const isFail = !res.skipped && !res.pass;
    const cls = res.skipped ? "stage skip" : res.pass ? "stage pass" : "stage fail";
    const mark = res.skipped ? "–" : res.pass ? "✓" : "✗";
    const chip = el("span", { class: cls }, [`${mark} ${s}`]);
    if (isFail) {
      chip.setAttribute("role", "button");
      chip.addEventListener("click", () => {
        const next = chip.nextElementSibling;
        if (next && next.classList.contains("stage-evidence")) {
          next.remove();
        } else {
          chip.insertAdjacentElement(
            "afterend",
            el("pre", { class: "stage-evidence" }, [res.output || "(no output)"]),
          );
        }
      });
    }
    return chip;
  });
  if (chips.length === 0) return "";
  return el("div", { class: "stage-row" }, chips);
}

// failureBlock surfaces why a run failed: the first failing gate stage and its
// captured output, plus the agent's own log (which carries crashes that abort
// before any gate runs). Mirrors the desktop failureBlock.
function failureBlock(attempt: Attempt): HTMLElement | string {
  const stages = attempt.evidence?.stages ?? {};
  const failed = STAGE_ORDER.find((s) => stages[s] && !stages[s].skipped && !stages[s].pass);
  const children: HTMLElement[] = [];
  let stageOut = "";
  if (failed) {
    const res = stages[failed];
    stageOut = (res.output ?? "").trim();
    children.push(el("p", { class: "fail-head" }, [`✗ ${failed} failed (exit ${res.exitCode})`]));
    children.push(
      stageOut
        ? el("pre", { class: "fail-out" }, [res.output.slice(-8000)])
        : el("p", { class: "meta" }, [`(command exited ${res.exitCode} with no output — check the agent log below)`]),
    );
  }
  const log = (attempt.log ?? "").trim();
  if (log) {
    children.push(
      el("details", { open: !stageOut }, [
        el("summary", {}, ["Agent log"]),
        el("pre", { class: "fail-out" }, [log.slice(-8000)]),
      ]),
    );
  }
  if (children.length === 0) return "";
  return el("div", { class: "failure" }, children);
}

// usageLine surfaces an attempt's self-reported token usage, or nothing when
// absent/zero (older payloads, or runs that captured none).
function usageLine(usage: Attempt["usage"]): HTMLElement | string {
  if (!usage || !usage.totalTokens) return "";
  return el("div", { class: "meta token-usage" }, [`tokens: ${usage.totalTokens.toLocaleString()}`]);
}

// commentsBlock lazily loads the task's comment thread and offers a composer —
// the same conversation the desktop UI shows, so guidance and history are
// visible before an accept/reject call rather than living only on the desktop.
function commentsBlock(id: string, conn: Connection): HTMLElement {
  const list = el("div", { class: "comment-list" }, [el("div", { class: "meta" }, ["Loading comments…"])]);
  const input = el("textarea", { placeholder: "Add a comment…" }) as HTMLTextAreaElement;

  const render = (comments: Comment[]) => {
    list.replaceChildren();
    const system = comments.filter((c) => c.authorType === "system");
    const regular = comments.filter((c) => c.authorType !== "system");
    if (system.length) {
      list.append(el("div", { class: "timeline meta" }, [system.map((c) => c.body).join(" → ")]));
    }
    if (regular.length === 0 && system.length === 0) {
      list.append(el("div", { class: "meta" }, ["No comments yet."]));
      return;
    }
    for (const c of regular) {
      list.append(commentRow(c));
    }
  };

  const load = async () => {
    try {
      render(await conn.api<Comment[]>("GET", `/api/tasks/${id}/comments`));
    } catch {
      list.replaceChildren(el("div", { class: "meta" }, ["Couldn't load comments."]));
    }
  };

  const send = el("button", {}, ["Comment"]) as HTMLButtonElement;
  send.onclick = async () => {
    const body = input.value.trim();
    if (!body) return;
    send.disabled = true;
    try {
      await conn.api("POST", `/api/tasks/${id}/comments`, { body });
      input.value = "";
      await load();
    } catch (e) {
      alert((e as Error).message);
    }
    send.disabled = false;
  };

  void load();
  return el("details", { class: "comments" }, [
    el("summary", {}, ["Comments"]),
    list,
    input,
    el("div", { class: "actions" }, [send]),
  ]);
}

function auditCard(r: ReviewItem, conn: Connection, act: Act): HTMLElement {
  const t = r.task;
  return el("div", { class: "card" }, [
    el("h3", {}, [t.title]),
    el("p", { class: "meta" }, ["Auto-merged — sampled for a human spot check."]),
    r.attempt?.evidence?.diff ? diffView(r.attempt.evidence.diff) : "",
    el("div", { class: "actions" }, [
      act("Looks good", "approve", () => conn.api("POST", `/api/tasks/${t.id}/audit-ok`)),
      act("Revert it", "danger", () => conn.api("POST", `/api/tasks/${t.id}/revert`)),
    ]),
  ]);
}
